// coach/components/dashboard/CoachDashboard.jsx
import React, { useState } from 'react';
import QuickActions from './QuickActions';
import TodaysSnapshot from './TodaysSnapshot';
import TeamsRosters from '../teams/TeamsRosters';
import Schedule from '../schedule/Schedule';
import CoachMessages from '../messages/CoachMessages';
import { MessageSquare, Users, Calendar, BookOpen, BarChart3 } from 'lucide-react';

/**
 * In-app dashboard tabs; message counts come from parent CoachLayout (single useCoachInboxUnread subscription).
 */
const CoachDashboard = ({ userData, rosters, schedule, onRefresh, inbox }) => {
  const [selectedTeam, setSelectedTeam] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');

  if (!userData || !inbox) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading dashboard…</p>
      </div>
    );
  }

  const coachData = userData;
  const totalUnreadMessages = inbox.totalUnread;
  const messages = inbox.teamMessages;
  const portalUnread = inbox.portalUnread;

  const getNextEvent = () => {
    const now = new Date();
    return schedule
      .filter((event) => new Date(event.date) > now)
      .sort((a, b) => new Date(a.date) - new Date(b.date))[0];
  };

  const filteredRosters = selectedTeam
    ? rosters.filter((roster) => roster.id === selectedTeam)
    : rosters;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'teams', label: 'Teams & Rosters', icon: Users },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
    {
      id: 'messages',
      label: 'Messages',
      icon: MessageSquare,
      badge: totalUnreadMessages,
    },
    { id: 'resources', label: 'Resources', icon: BookOpen },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm relative whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                  {typeof tab.badge === 'number' && tab.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                      {tab.badge > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            <QuickActions
              coachData={coachData}
              teams={rosters}
              onRefresh={onRefresh}
            />
            <TodaysSnapshot
              nextEvent={getNextEvent()}
              unreadMessages={totalUnreadMessages}
              teams={rosters}
            />
          </div>
        )}

        {activeTab === 'teams' && (
          <TeamsRosters
            rosters={filteredRosters}
            coachData={coachData}
            onRefresh={onRefresh}
          />
        )}

        {activeTab === 'schedule' && (
          <Schedule
            schedule={schedule}
            rosters={rosters}
            coachData={coachData}
            onRefresh={onRefresh}
          />
        )}

        {activeTab === 'messages' && (
          <CoachMessages
            coachData={coachData}
            rosters={rosters}
            messages={messages}
            portalUnread={portalUnread}
          />
        )}

        {activeTab === 'resources' && (
          <>
            <h1 className="text-center p-4 text-2xl">Comming Soon...</h1>
          </>
        )}
      </div>
    </div>
  );
};

export default CoachDashboard;
